import { v4 as uuidv4 } from "uuid";

import * as APIModel from "entities/ApiModel";
import * as UIModel from "entities/UIModel";
import { hasNotNullOrEmptyProp, isValidDate, mapToUiMessage, mapUIPricingDetail } from "./helper";
import { convertToNumber, formatDateRegEx, formatDateToJSONString, hasValue, parseDateString } from "utils/shared";
import { OrderStatusType, UWASpecialRisk } from "utils/data/enum";
import { SelectFieldOption } from "controls/global/select-field/SelectInput";

// TODO - make sure it's done here 
// find me robun 10/18/2025


//#region Map API to UI
const mapToUiUwa = (p:APIModel.UWAProduct) => {
  let uiMessages: UIModel.Message[] | undefined;
  if (p.Messages && p.Messages.length > 0) {
    uiMessages = p.Messages.map((apiMessage) => mapToUiMessage(apiMessage));
  }

  const isUwaProductSubmitted = !!p?.OrderStatusTypeCode && p.OrderStatusTypeCode !== OrderStatusType.Pending;

  const uwaProduct: UIModel.UWAProduct = {
    isDirty: p.IsDirty,
    isDefault: true, // false, -- must be set to true for saved pending product
    integrationKey: p.IntegrationKey,
    identityKey: p.IdentityKey,
    productID: p.ProductID,
    fileID: p.FileID,
    orderID: p.OrderID,
    isHidden: p.IsHidden,
    notificationEmailAddresses: p.NotificationEmailAddresses,
    uwaRequestSummaryDocumentID: p.UWARequestSummaryDocumentID,
    orderStatusTypeCode: p.OrderStatusTypeCode || "",
    orderStatusTypeName: p.OrderStatusTypeName,
    submittedDate: p.SubmittedDate ? formatDateRegEx(p.SubmittedDate) : "",
    submittedByUserID: p.SubmittedByUserID,
    submittedByUserName: p.SubmittedByUserName,
    cancelledDate: p.CancelledDate ? formatDateRegEx(p.CancelledDate) : "",
    cancelledByUserID: p.CancelledByUserID,
    cancelledByUserName: p.CancelledByUserName,
    undoCancellationDate: p.UndoCancellationDate ? formatDateRegEx(p.UndoCancellationDate) : "", 
    undoCancellationByUserID: p.UndoCancellationByUserID,
    undoCancellationByUserName: p.UndoCancellationByUserName,
    lastErrorMessage: p.LastErrorMessage,
    originationSource: p.OriginationSource,
    originationUser: p.OriginationUser,
    displayOrder: p.DisplayOrder,
    messages: uiMessages,
    pricingDetail: p.PricingDetail ? mapUIPricingDetail(p.PricingDetail) : undefined,
    uwaRequest: p.UWARequest ? mapUiUwaRequest(p.UWARequest) : undefined,
    uwaRequestInfo: p.UWARequestInfo ? mapUiUwaRequestInfo(p.UWARequestInfo, isUwaProductSubmitted) : undefined,
    uwaPolicyInfos: p.UWAPolicyInfos ? mapUiUwaPolicyInfos(p.UWAPolicyInfos, (p.UWARequest?.UWARequestID || 0), isUwaProductSubmitted ) : undefined,
  };

  return uwaProduct;

}

const mapUiUwaRequest = (uwaRequest: APIModel.UWARequest) => {

  const mapPropertyTypes = (uwaPropertyTypeCodes: string[] | undefined) => {
    // // moackup data
    // uwaPropertyTypeCodes = ["AGRICULTURAL_LAND", "COOP"];

    if (!uwaPropertyTypeCodes || uwaPropertyTypeCodes?.length === 0) {
      return [];
    }

    const propertyTypes: Array<SelectFieldOption> =
      uwaPropertyTypeCodes
        ?.filter((code) => code)
        .map((code) => {
          const propertyType: SelectFieldOption = {
            value: code,
            text: "",
          }

          return propertyType;
        });

    return propertyTypes;
    
    
  }

  const apiUwaReqquest: UIModel.UWARequest = {
    uwaRequestID: uwaRequest.UWARequestID,
    uwaRequestNumber: uwaRequest.UWARequestNumber,
    identityKey: uwaRequest.IdentityKey,
    integrationKey: uwaRequest.IntegrationKey,
    anticipatedClosingDate: parseDateString(uwaRequest.AnticipatedClosingDate),
    titleExaminedFrom: parseDateString(uwaRequest.TitleExaminedFrom),
    titleExaminedTo: parseDateString(uwaRequest.TitleExaminedTo),
    projectReferenceNumber: uwaRequest.ProjectReferenceNumber,
    priorApprovalNumber: uwaRequest.PriorApprovalNumber,
    ownerID: uwaRequest.OwnerID,
    summary: uwaRequest.Summary,
    preferredUnderwriter: uwaRequest.PreferredUnderwriter,
    preferredUnderwriterName: uwaRequest.PreferredUnderwriterName,
    propertyTypes: mapPropertyTypes(uwaRequest.UWAPropertyTypeCodes),
    ownerName: uwaRequest.OwnerName,
    uwaRequestStatusTypeCode: uwaRequest.UWARequestStatusTypeCode,
    uwaRequestStatusTypeName: uwaRequest.UWARequestStatusTypeName,
    cancellationDate: uwaRequest?.CancellationDate ? formatDateRegEx(uwaRequest.CancellationDate) : "",
  }

  return apiUwaReqquest;

}

const mapUiUwaRequestInfo = (uwaRequestInfo: APIModel.UWARequestInfo, isUwaProductSubmitted: boolean) => {

  const mapSpecialRisks = () => {
    const specialRisks: Array<SelectFieldOption> = [];
    const addToSpecialRisks = (isSpecialRisk: boolean | null | undefined, specialRiskValue: string)  => {
      if (isSpecialRisk || false) {
        specialRisks.push({ value: specialRiskValue, text: "" });  
      }
    }

    addToSpecialRisks(uwaRequestInfo.IsConstructionLoan, UWASpecialRisk.IsConstructionLoan);
    addToSpecialRisks(uwaRequestInfo.IsBrokenPriority, UWASpecialRisk.IsBrokenPriority);
    addToSpecialRisks(uwaRequestInfo.IsRecentConstruction, UWASpecialRisk.IsRecentConstruction);
    addToSpecialRisks(uwaRequestInfo.HasMechanicsLienCoverageRequest, UWASpecialRisk.HasMechanicsLienCoverageRequest);
    addToSpecialRisks(uwaRequestInfo.IsTidelands, UWASpecialRisk.IsTidelands);
    addToSpecialRisks(uwaRequestInfo.IsRailroadProperty, UWASpecialRisk.IsRailroadProperty);
    addToSpecialRisks(uwaRequestInfo.IsJudicialProceeding, UWASpecialRisk.IsJudicialProceeding);
    addToSpecialRisks(uwaRequestInfo.IsForeclosure, UWASpecialRisk.IsForeclosure);
    addToSpecialRisks(uwaRequestInfo.HasMineralCoverage, UWASpecialRisk.HasMineralCoverage);
    addToSpecialRisks(uwaRequestInfo.IsLoanConstructionForeclosure, UWASpecialRisk.IsLoanConstructionForeclosure);
    addToSpecialRisks(uwaRequestInfo.IsEasementIndependantOfProperty, UWASpecialRisk.IsEasementIndependantOfProperty);
    addToSpecialRisks(uwaRequestInfo.HasInsuredOption, UWASpecialRisk.HasInsuredOption);
    addToSpecialRisks(uwaRequestInfo.IsNativeAmericanLand, UWASpecialRisk.IsNativeAmericanLand);
    addToSpecialRisks(uwaRequestInfo.HasWaterRights, UWASpecialRisk.HasWaterRights);
    addToSpecialRisks(uwaRequestInfo.HasSheriffSale, UWASpecialRisk.HasSheriffSale);
    addToSpecialRisks(uwaRequestInfo.IsInsuringRecordedLien, UWASpecialRisk.IsInsuringRecordedLien);
    addToSpecialRisks(uwaRequestInfo.IsAssignmentOfInterest, UWASpecialRisk.IsAssignmentOfInterest);
    addToSpecialRisks(uwaRequestInfo.HasPurchaseContract, UWASpecialRisk.HasPurchaseContract);
    addToSpecialRisks(uwaRequestInfo.HasRelianceOnIndemnity, UWASpecialRisk.HasRelianceOnIndemnity);
    addToSpecialRisks(uwaRequestInfo.HasSurveyConcerns, UWASpecialRisk.HasSurveyConcerns);
    addToSpecialRisks(uwaRequestInfo.IsOwnerOutOfPossession, UWASpecialRisk.IsOwnerOutOfPossession);
    addToSpecialRisks(uwaRequestInfo.HasOtherExtraHazardRisk, UWASpecialRisk.HasOtherExtraHazardRisk);
    addToSpecialRisks(uwaRequestInfo.HasLitigation, UWASpecialRisk.HasLitigation);
    addToSpecialRisks(uwaRequestInfo.HasTransactionTurnedDown, UWASpecialRisk.HasTransactionTurnedDown);
    addToSpecialRisks(uwaRequestInfo.IsEnergyProject, UWASpecialRisk.IsEnergyProject);
    addToSpecialRisks(uwaRequestInfo.IsCannabisProject, UWASpecialRisk.IsCannabisProject);
    addToSpecialRisks(uwaRequestInfo.IsHardMoney, UWASpecialRisk.IsHardMoney);
    addToSpecialRisks(uwaRequestInfo.HasUnusualCoverages, UWASpecialRisk.HasUnusualCoverages);
    addToSpecialRisks(uwaRequestInfo.HasCoInsurance, UWASpecialRisk.HasCoInsurance);

    if (!isUwaProductSubmitted) {
      // Add an empty special risk
      addToSpecialRisks(false, "");
    }

    return specialRisks;
  }  

 const uIUwaRequestInfo: UIModel.UWARequestInfo = {
    integrationKey: uwaRequestInfo.IntegrationKey,
    uwaRequestInfoId: uwaRequestInfo.UWARequestInfoID,
    uwaRequestID: uwaRequestInfo.UWARequestID,
    isMultiSite: uwaRequestInfo.IsMultiSite,
    isNoSpecialRisks: uwaRequestInfo.IsNoSpecialRisks || false,

    // Dynamically map boolean flags from UWASpecialRisk
    isConstructionLoan: uwaRequestInfo.IsConstructionLoan, 
    isBrokenPriority: uwaRequestInfo.IsBrokenPriority, 
    isRecentConstruction: uwaRequestInfo.IsRecentConstruction, 
    hasMechanicsLienCoverageRequest: uwaRequestInfo.HasMechanicsLienCoverageRequest, 
    isTidelands: uwaRequestInfo.IsTidelands, 
    isRailroadProperty: uwaRequestInfo.IsRailroadProperty, 
    isJudicialProceeding: uwaRequestInfo.IsJudicialProceeding, 
    isForeclosure: uwaRequestInfo.IsForeclosure,
    hasMineralCoverage: uwaRequestInfo.HasMineralCoverage, 
    isLoanConstructionForeclosure: uwaRequestInfo.IsLoanConstructionForeclosure, 
    isEasementIndependantOfProperty: uwaRequestInfo.IsEasementIndependantOfProperty, 
    hasInsuredOption: uwaRequestInfo.HasInsuredOption, 
    isNativeAmericanLand: uwaRequestInfo.IsNativeAmericanLand, 
    hasWaterRights: uwaRequestInfo.HasWaterRights, 
    hasSheriffSale: uwaRequestInfo.HasSheriffSale, 
    isInsuringRecordedLien: uwaRequestInfo.IsInsuringRecordedLien, 
    isAssignmentOfInterest: uwaRequestInfo.IsAssignmentOfInterest, 
    hasPurchaseContract: uwaRequestInfo.HasPurchaseContract, 
    hasRelianceOnIndemnity: uwaRequestInfo.HasRelianceOnIndemnity, 
    hasSurveyConcerns: uwaRequestInfo.HasSurveyConcerns, 
    isOwnerOutOfPossession: uwaRequestInfo.IsOwnerOutOfPossession, 
    hasOtherExtraHazardRisk: uwaRequestInfo.HasOtherExtraHazardRisk, 
    hasLitigation: uwaRequestInfo.HasLitigation, 
    hasTransactionTurnedDown: uwaRequestInfo.HasTransactionTurnedDown, 
    isEnergyProject: uwaRequestInfo.IsEnergyProject, 
    isCannabisProject: uwaRequestInfo.IsCannabisProject, 
    isHardMoney: uwaRequestInfo.IsHardMoney, 
    hasUnusualCoverages: uwaRequestInfo.HasUnusualCoverages, 
    hasCoInsurance: uwaRequestInfo.HasCoInsurance,

    specialRisks: mapSpecialRisks(),
  };



  return uIUwaRequestInfo;
}

const mapUiUwaPolicyInfos = (uwaPolicyInfos: APIModel.UWAPolicyInfo[], uwaRequestID: number, isUwaProductSubmitted: boolean) => {

  let uiPolicyInfos: UIModel.UWAPolicyInfo[] = [];
  // const uiPolicyInfos: UIModel.UWAPolicyInfo[] =
  uwaPolicyInfos?.map((p) => {
    const uwaPolicyInfo: UIModel.UWAPolicyInfo = {
      identityKey: p.IdentityKey,
      integrationKey: p.IntegrationKey,
      uwaPolicyInfoID: p.UWAPolicyInfoID,
      uwaRequestID: p.UWARequestID,
      uwaPolicyTypeCode: p.UWAPolicyTypeCode,
      policyForm: p.PolicyForm,
      policyAmount: p.PolicyAmount,
      proposedInsured: p.ProposedInsured,
      endorsements: p.Endorsements,

      id: crypto.randomUUID(),  // check with Niha
    }

    // return uwaPolicyInfo;
    uiPolicyInfos.push(uwaPolicyInfo);
  })

    // Add a blank PolicyInfo item if it's not submitted
  if (!isUwaProductSubmitted) {
    uiPolicyInfos.push({
      identityKey: 0,
      integrationKey: uuidv4(),
      uwaPolicyInfoID: 0,
      uwaRequestID: uwaRequestID,
      uwaPolicyTypeCode: undefined,
      policyForm: undefined,
      policyAmount: undefined,
      proposedInsured: undefined,
      endorsements: undefined,

      id: crypto.randomUUID(), 
    })
    
  }

  // console.log("uiPolicyInfos:", uiPolicyInfos);
  return uiPolicyInfos;
}

//#endregion



//#region Map UI to API  
const isValidUwaPolicyInfo = (uwaPolicyInfo: UIModel.UWAPolicyInfo): boolean =>
  !uwaPolicyInfo.isDeleted &&
  hasNotNullOrEmptyProp(uwaPolicyInfo, ["uwaPolicyTypeCode", "proposedInsured", "endorsements", "policyAmount"]);


const mapApiUwaRequest = (uwaRequest: UIModel.UWARequest, fileID?: number, orderID?: number) => { //, uwaRequestInfo: UIModel.UWARequestInfo, uwaPolicyInfos: UIModel.UWAPolicyInfo[]) => {
  const apiUwaReqquest: APIModel.UWARequest = {
    IsDirty: false,
    FileID: fileID || 0,
    OrderID: orderID || 0,
    UWARequestID: uwaRequest.uwaRequestID || 0,
    IntegrationKey: uwaRequest.integrationKey || uuidv4(),
    AnticipatedClosingDate: uwaRequest.anticipatedClosingDate ? formatDateToJSONString(uwaRequest.anticipatedClosingDate, true) : null,
    TitleExaminedFrom:
      uwaRequest.titleExaminedFrom &&
        isValidDate(uwaRequest.titleExaminedFrom)
        ? formatDateToJSONString(uwaRequest.titleExaminedFrom, true)
        : null,
    TitleExaminedTo:
      uwaRequest.titleExaminedTo &&
        isValidDate(uwaRequest.titleExaminedTo)
        ? formatDateToJSONString(uwaRequest.titleExaminedTo, true)
        : null,    
    ProjectReferenceNumber: uwaRequest.projectReferenceNumber,
    PriorApprovalNumber: uwaRequest.priorApprovalNumber,
    OwnerID: uwaRequest.ownerID,
    Summary: uwaRequest.summary,
    UWARequestNumber: uwaRequest.uwaRequestNumber,
    PreferredUnderwriter: uwaRequest.preferredUnderwriter,
    UWAPropertyTypeCodes: uwaRequest.propertyTypes?.map((pt) => pt.value) || [],
    // UWARequestInfo: mapApiUwaRequestInfo(uwaRequestInfo, uwaRequest.uwaRequestID),
    // UWAPolicyInfos: mapApiUwaPolicyInfos(uwaPolicyInfos, uwaRequest.uwaRequestID),
  }

  return apiUwaReqquest;
}

const mapApiUwaRequestInfo = (uwaRequestInfo?: UIModel.UWARequestInfo, uwaRequestID?: number, fileID?: number, orderID?: number) => {
  if (!uwaRequestInfo) return undefined;

  // const specialRiskCodes = uwaRequestInfo.specialRisks
  //   ?.filter(o => o.value)
  //   ?.map(o => o.value.toLowerCase()) ?? [];

  // const hasRisk = (risk: string) => specialRiskCodes.includes(risk.toLowerCase());

  const apiUwaRequestInfo: APIModel.UWARequestInfo = {
    IsDirty: false,
    FileID: fileID || 0,
    OrderID: orderID || 0,
    IntegrationKey: uwaRequestInfo.integrationKey || uuidv4(),
    UWARequestInfoID: uwaRequestInfo.uwaRequestInfoId || 0,
    UWARequestID: uwaRequestInfo.uwaRequestID || uwaRequestID || 0,
    IsMultiSite: uwaRequestInfo.isMultiSite,
    IsNoSpecialRisks: uwaRequestInfo.isNoSpecialRisks,

    // Dynamically map boolean flags from UWASpecialRisk
    IsConstructionLoan: uwaRequestInfo.isConstructionLoan, // hasRisk(UWASpecialRisk.IsConstructionLoan),
    IsBrokenPriority: uwaRequestInfo.isBrokenPriority, // hasRisk(UWASpecialRisk.IsBrokenPriority),
    IsRecentConstruction: uwaRequestInfo.isRecentConstruction, // hasRisk(UWASpecialRisk.IsRecentConstruction),
    HasMechanicsLienCoverageRequest: uwaRequestInfo.hasMechanicsLienCoverageRequest, // hasRisk(UWASpecialRisk.HasMechanicsLienCoverageRequest),
    IsTidelands: uwaRequestInfo.isTidelands, // hasRisk(UWASpecialRisk.IsTidelands),
    IsRailroadProperty: uwaRequestInfo.isRailroadProperty, // hasRisk(UWASpecialRisk.IsRailroadProperty),
    IsJudicialProceeding: uwaRequestInfo.isJudicialProceeding, // hasRisk(UWASpecialRisk.IsJudicialProceeding),
    IsForeclosure: uwaRequestInfo.isForeclosure, // hasRisk(UWASpecialRisk.IsForeclosure),
    HasMineralCoverage: uwaRequestInfo.hasMineralCoverage, // hasRisk(UWASpecialRisk.HasMineralCoverage),
    IsLoanConstructionForeclosure: uwaRequestInfo.isLoanConstructionForeclosure, // hasRisk(UWASpecialRisk.IsLoanConstructionForeclosure),
    IsEasementIndependantOfProperty: uwaRequestInfo.isEasementIndependantOfProperty, // hasRisk(UWASpecialRisk.IsEasementIndependantOfProperty),
    HasInsuredOption: uwaRequestInfo.hasInsuredOption, // hasRisk(UWASpecialRisk.HasInsuredOption),
    IsNativeAmericanLand: uwaRequestInfo.isNativeAmericanLand, // hasRisk(UWASpecialRisk.IsNativeAmericanLand),
    HasWaterRights: uwaRequestInfo.hasWaterRights, // hasRisk(UWASpecialRisk.HasWaterRights),
    HasSheriffSale: uwaRequestInfo.hasSheriffSale, // hasRisk(UWASpecialRisk.HasSheriffSale),
    IsInsuringRecordedLien: uwaRequestInfo.isInsuringRecordedLien, // hasRisk(UWASpecialRisk.IsInsuringRecordedLien),
    IsAssignmentOfInterest: uwaRequestInfo.isAssignmentOfInterest, // hasRisk(UWASpecialRisk.IsAssignmentOfInterest),
    HasPurchaseContract: uwaRequestInfo.hasPurchaseContract, // hasRisk(UWASpecialRisk.HasPurchaseContract),
    HasRelianceOnIndemnity: uwaRequestInfo.hasRelianceOnIndemnity, // hasRisk(UWASpecialRisk.HasRelianceOnIndemnity),
    HasSurveyConcerns: uwaRequestInfo.hasSurveyConcerns, // hasRisk(UWASpecialRisk.HasSurveyConcerns),
    IsOwnerOutOfPossession: uwaRequestInfo.isOwnerOutOfPossession, // hasRisk(UWASpecialRisk.IsOwnerOutOfPossession),
    HasOtherExtraHazardRisk: uwaRequestInfo.hasOtherExtraHazardRisk, // hasRisk(UWASpecialRisk.HasOtherExtraHazardRisk),
    HasLitigation: uwaRequestInfo.hasLitigation, // hasRisk(UWASpecialRisk.HasLitigation),
    HasTransactionTurnedDown: uwaRequestInfo.hasTransactionTurnedDown, // hasRisk(UWASpecialRisk.HasTransactionTurnedDown),
    IsEnergyProject: uwaRequestInfo.isEnergyProject, // hasRisk(UWASpecialRisk.IsEnergyProject),
    IsCannabisProject: uwaRequestInfo.isCannabisProject, // hasRisk(UWASpecialRisk.IsCannabisProject),
    IsHardMoney: uwaRequestInfo.isHardMoney, // hasRisk(UWASpecialRisk.IsHardMoney),
    HasUnusualCoverages: uwaRequestInfo.hasUnusualCoverages, // hasRisk(UWASpecialRisk.HasUnusualCoverages),
    HasCoInsurance: uwaRequestInfo.hasCoInsurance, // hasRisk(UWASpecialRisk.HasCoInsurance),
  };



  return apiUwaRequestInfo;
}

const mapUiUwaSpecialRisks = (uwaRequestInfo?: UIModel.UWARequestInfo) => {
  if (!uwaRequestInfo) return undefined;

  const specialRiskCodes = uwaRequestInfo.specialRisks
    ?.filter(o => o.value)
    ?.map(o => o.value.toLowerCase()) ?? [];

  const hasRisk = (risk: string) => specialRiskCodes.includes(risk.toLowerCase());

  const uiUwaRequestInfo: UIModel.UWARequestInfo = {
    // Dynamically map boolean flags from UWASpecialRisk
    ...uwaRequestInfo,
    isConstructionLoan: hasRisk(UWASpecialRisk.IsConstructionLoan),
    isBrokenPriority: hasRisk(UWASpecialRisk.IsBrokenPriority),
    isRecentConstruction: hasRisk(UWASpecialRisk.IsRecentConstruction),
    hasMechanicsLienCoverageRequest: hasRisk(UWASpecialRisk.HasMechanicsLienCoverageRequest),
    isTidelands: hasRisk(UWASpecialRisk.IsTidelands),
    isRailroadProperty: hasRisk(UWASpecialRisk.IsRailroadProperty),
    isJudicialProceeding: hasRisk(UWASpecialRisk.IsJudicialProceeding),
    isForeclosure: hasRisk(UWASpecialRisk.IsForeclosure),
    hasMineralCoverage: hasRisk(UWASpecialRisk.HasMineralCoverage),
    isLoanConstructionForeclosure: hasRisk(UWASpecialRisk.IsLoanConstructionForeclosure),
    isEasementIndependantOfProperty: hasRisk(UWASpecialRisk.IsEasementIndependantOfProperty),
    hasInsuredOption: hasRisk(UWASpecialRisk.HasInsuredOption),
    isNativeAmericanLand: hasRisk(UWASpecialRisk.IsNativeAmericanLand),
    hasWaterRights: hasRisk(UWASpecialRisk.HasWaterRights),
    hasSheriffSale: hasRisk(UWASpecialRisk.HasSheriffSale),
    isInsuringRecordedLien: hasRisk(UWASpecialRisk.IsInsuringRecordedLien),
    isAssignmentOfInterest: hasRisk(UWASpecialRisk.IsAssignmentOfInterest),
    hasPurchaseContract: hasRisk(UWASpecialRisk.HasPurchaseContract),
    hasRelianceOnIndemnity: hasRisk(UWASpecialRisk.HasRelianceOnIndemnity),
    hasSurveyConcerns: hasRisk(UWASpecialRisk.HasSurveyConcerns),
    isOwnerOutOfPossession: hasRisk(UWASpecialRisk.IsOwnerOutOfPossession),
    hasOtherExtraHazardRisk: hasRisk(UWASpecialRisk.HasOtherExtraHazardRisk),
    hasLitigation: hasRisk(UWASpecialRisk.HasLitigation),
    hasTransactionTurnedDown: hasRisk(UWASpecialRisk.HasTransactionTurnedDown),
    isEnergyProject: hasRisk(UWASpecialRisk.IsEnergyProject),
    isCannabisProject: hasRisk(UWASpecialRisk.IsCannabisProject),
    isHardMoney: hasRisk(UWASpecialRisk.IsHardMoney),
    hasUnusualCoverages: hasRisk(UWASpecialRisk.HasUnusualCoverages),
    hasCoInsurance: hasRisk(UWASpecialRisk.HasCoInsurance),
  };

  return uiUwaRequestInfo;
}

const mapApiUwaPolicyInfos = (uwaPolicyInfos?: UIModel.UWAPolicyInfo[], uwaRequestID?: number, fileID?: number, orderID?: number ) => {
  if (!uwaPolicyInfos) return undefined;

  const isValidPolicyInfo = (uwaPolicyInfo: UIModel.UWAPolicyInfo) => {
    if (uwaPolicyInfo.uwaPolicyTypeCode && hasValue(uwaPolicyInfo.policyAmount)) {
      return true;
    }
    return false;
  }

  const apiUwaPolicyInfos: APIModel.UWAPolicyInfo[] =
    uwaPolicyInfos
      .filter((p) => isValidPolicyInfo(p))
      .map((p) => {
        const apiUwaPolicyInfo: APIModel.UWAPolicyInfo = {
          IsDirty: false,
          FileID: fileID || 0,
          OrderID: orderID || 0,
          IdentityKey: p.identityKey || 0,
          IntegrationKey: p.integrationKey, // || uuidv4(),
          UWAPolicyInfoID: p.uwaPolicyInfoID || 0,
          UWARequestID: p.uwaRequestID || uwaRequestID || 0,
          UWAPolicyTypeCode: p.uwaPolicyTypeCode,
          PolicyForm: p.policyForm,
          PolicyAmount: convertToNumber(p.policyAmount),
          ProposedInsured: p.proposedInsured,
          // Endorsement: p.endorsement,
          Endorsements: p.endorsements,
        };
        return apiUwaPolicyInfo;
      })

  return apiUwaPolicyInfos;
}
//#endregion Map UI to API  


export {
  mapToUiUwa,
  mapUiUwaSpecialRisks,
  mapApiUwaRequest,
  mapApiUwaRequestInfo,
  mapApiUwaPolicyInfos,
  isValidUwaPolicyInfo,

}
